import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Feather, Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaSpacing } from '../../styles/theme';
import { formatDuration } from '../../utils/formatDuration';

interface FactsLineProps {
  /** Total time in minutes (`recipe.readyInMinutes`). */
  totalTime?: number;
  /** The time boxes above carry it: leave the clock segment out. */
  hideTime?: boolean;
  servings?: number;
  /** True when the recipe sits in an upcoming meal slot. */
  isPlanned?: boolean;
  /** Human slot label for the planned pill, e.g. "Mercredi soir". */
  slotLabel?: string;
  /** Mobile sizing (13 instead of 14). */
  small?: boolean;
}

/**
 * The single line of facts under the title — time, servings, and the planned
 * slot when there is one. Replaces the old PREP / TOTAL stat pills: one number
 * for time, nothing else, so the eye reaches the ingredients faster.
 */
export const FactsLine: React.FC<FactsLineProps> = ({
  totalTime,
  hideTime,
  servings,
  isPlanned,
  slotLabel,
  small,
}) => {
  const { t } = useTranslation();

  const hasTime = !hideTime && typeof totalTime === 'number' && totalTime > 0;
  const hasServings = typeof servings === 'number' && servings > 0;
  const showSlot = !!(isPlanned && slotLabel);

  if (!hasTime && !hasServings && !showSlot) return null;

  const textStyle = small ? styles.textSmall : styles.text;
  const segments: React.ReactNode[] = [];

  if (hasTime) {
    segments.push(
      <View key="time" style={styles.segment}>
        <Feather name="clock" size={small ? 14 : 15} color={Paprika.textSecondary} />
        <Text style={[textStyle, styles.strong]}>{formatDuration(totalTime!, t)}</Text>
      </View>
    );
  }

  if (hasServings) {
    segments.push(
      <Text key="servings" style={[textStyle, styles.strong]}>
        {t('recipeDetail.servingsCount', {
          defaultValue: '{{count}} portions',
          count: servings,
        })}
      </Text>
    );
  }

  if (showSlot) {
    segments.push(
      <View key="slot" style={styles.slotPill}>
        <Ionicons name="calendar" size={13} color={Paprika.accent} />
        <Text style={[styles.slotPillText, small && styles.slotPillTextSmall]}>{slotLabel}</Text>
      </View>
    );
  }

  return (
    <View style={styles.row} testID="facts-line">
      {segments.map((segment, index) => (
        <React.Fragment key={index}>
          {index > 0 && <Text style={[textStyle, styles.separator]}>·</Text>}
          {segment}
        </React.Fragment>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  segment: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  text: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    lineHeight: 25,
    color: Paprika.textSecondary,
  },
  textSmall: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    lineHeight: 23,
    color: Paprika.textSecondary,
  },
  strong: {
    fontFamily: 'Inter-Medium',
  },
  separator: {
    color: Paprika.textMuted,
    paddingHorizontal: PaprikaSpacing.sm,
  },
  slotPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 3,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: Paprika.brandTint,
    borderWidth: 1,
    borderColor: Paprika.brandSoft,
  },
  slotPillText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    color: Paprika.accent,
  },
  slotPillTextSmall: {
    fontSize: 11.5,
  },
});

export default FactsLine;
